/**
 * Reactive source — the one subscription system
 *
 * Every store in the library keeps its own state and its own idea of what
 * "changed" means. paramStore compares JSON signatures because Paramate hands
 * back fresh objects every configure; sysData compares field by field; a
 * reactiveStore just takes the write at face value. None of that belongs here.
 *
 * What does belong here is everything downstream of that decision, which used
 * to be four near-copies of the same code:
 *
 *   - per-key subscribers, and multi-key subscribers that fire once
 *   - whole-source subscribers
 *   - batching several writes into one commit
 *   - not letting one throwing callback take out the rest
 *
 * A store owns its writes and calls `commit(changedKeys)` when it has worked
 * out what moved. Everything else — reactiveStore (store.ts), appData
 * (data/projectManagement.ts), paramStore, and the derived stores behind
 * `.derive()` — is a different way of holding state on top of this one file.
 *
 * `commit` always notifies whole-source subscribers, even for an empty key set:
 * a store that wants to stay quiet when nothing changed simply doesn't call it.
 */

/**
 * Per-key subscriber bookkeeping. Its own function rather than inline in
 * `createSource` because a store occasionally wants the bare fan-out with no
 * commit protocol around it.
 */
export function createKeyedNotifier() {
  const keySubscribers = new Map<string, Set<(value: any, key: string) => void>>();

  return {
    subscribeKey(key: string, cb: (value: any, key: string) => void): () => void {
      let subs = keySubscribers.get(key);
      if (!subs) { subs = new Set(); keySubscribers.set(key, subs); }
      subs.add(cb);
      return () => { keySubscribers.get(key)?.delete(cb); };
    },
    notifyKey(key: string, value: any): void {
      const subs = keySubscribers.get(key);
      if (!subs || subs.size === 0) return;
      const failures: Array<{ cb: (value: any, key: string) => void; err: unknown }> = [];
      subs.forEach(cb => {
        try { cb(value, key); }
        catch (err) { failures.push({ cb, err }); }
      });
      failures.forEach(({ cb, err }) => console.error(`[STORE] subscribeKey('${key}') callback threw:`, err, cb));
    },
  };
}

export interface SourceSpec<S> {
  /** Shown in the console when a subscriber throws, e.g. 'PARAMS'. */
  name: string;
  /** The whole current state, handed to whole-source subscribers. */
  snapshot(): S;
  /** Turn a subscribe target into the value the callback receives. */
  resolve(target: any): any;
  /** Which keys a target depends on, so it can be woken when they move. */
  keysOf(target: any): string[];
}

export interface SubscribeOpts {
  /**
   * Whether a subscriber watching several keys fires once for a commit that
   * moved more than one of them. Default true. appData passes false — its
   * bindings have always recomputed once per changed key, and firing once
   * instead would be a silent behaviour change in existing apps.
   */
  coalesce?: boolean;
}

export interface Source<S> {
  /**
   * One key, several keys, or (a bare function) the whole source. Returns an
   * unsubscribe. Never fires immediately — the element-level binders in
   * components/base.ts are what apply straight away.
   *
   * Keyed callbacks get `(resolvedValue, target)`; whole-source callbacks get
   * `(snapshot, changedKeys)`.
   */
  subscribe(target: any, cb?: any, opts?: SubscribeOpts): () => void;
  /**
   * Announce a set of changed keys. Wakes the keyed subscribers watching any
   * of them, then every whole-source subscriber. Inside `batch()` the keys are
   * held back and folded into the one commit at the end.
   */
  commit(changedKeys: Iterable<string>): void;
  /**
   * Run `fn`, holding every commit it causes until it returns, then fire them
   * as one. Nested calls join the outermost batch. A batch that changed
   * nothing commits nothing — not even to whole-source subscribers.
   */
  batch<T>(fn: () => T): T;
  /** The value a subscriber for this target would be handed. */
  resolve(target: any): any;
  /**
   * Run `hook` at the start of every commit, before any subscriber. It may add
   * keys to the set it is given, which is how derived keys join the commit
   * their dependencies caused instead of arriving in one of their own.
   */
  onCommit(hook: (changedKeys: Set<string>) => void): void;
}

export function createSource<S>(spec: SourceSpec<S>): Source<S> {
  const notifier = createKeyedNotifier();
  const wholeSubscribers = new Set<(state: S, changedKeys: ReadonlySet<string>) => void>();
  const commitHooks: Array<(changedKeys: Set<string>) => void> = [];

  /** Bumped once per commit, so a subscriber watching several keys fires once
   *  for the whole commit rather than once per changed key. */
  let seq = 0;

  /** Depth of nested batch() calls, and the keys they have collected so far. */
  let batchDepth = 0;
  let batched: Set<string> | null = null;

  /** Wraps a callback so it runs at most once per commit. */
  const oncePerCommit = (fn: () => void): (() => void) => {
    let seen = -1;
    return () => {
      if (seen === seq) return;
      seen = seq;
      fn();
    };
  };

  function fire(changed: Set<string>): void {
    // Hooks first, and they may grow the set — see onCommit.
    for (const hook of commitHooks) hook(changed);

    seq++;
    for (const key of changed) notifier.notifyKey(key, undefined);

    if (wholeSubscribers.size === 0) return;
    const snapshot = spec.snapshot();
    wholeSubscribers.forEach(cb => {
      try { cb(snapshot, changed); }
      catch (err) { console.error(`[${spec.name}] whole-source subscriber threw:`, err, cb); }
    });
  }

  return {
    subscribe(target: any, cb?: any, opts?: SubscribeOpts): () => void {
      if (typeof target === 'function') {
        const subscriber = target as (state: S, changedKeys: ReadonlySet<string>) => void;
        wholeSubscribers.add(subscriber);
        return () => { wholeSubscribers.delete(subscriber); };
      }

      // The notified value is ignored: the callback re-reads through
      // spec.resolve, which is the one place that knows how to turn this
      // target into a value. Passing one here would just be a staler copy.
      const run = () => cb(spec.resolve(target), target);
      const wake = opts?.coalesce === false ? run : oncePerCommit(run);
      const unsubs = spec.keysOf(target).map(key => notifier.subscribeKey(key, wake));
      return () => unsubs.forEach(unsub => unsub());
    },

    commit(changedKeys: Iterable<string>): void {
      if (batched) { for (const key of changedKeys) batched.add(key); return; }
      fire(new Set(changedKeys));
    },

    batch<T>(fn: () => T): T {
      if (batchDepth++ === 0) batched = new Set();
      try {
        return fn();
      } finally {
        if (--batchDepth === 0) {
          const changed = batched!;
          batched = null;
          if (changed.size > 0) fire(changed);
        }
      }
    },

    resolve: spec.resolve,

    onCommit(hook: (changedKeys: Set<string>) => void): void {
      commitHooks.push(hook);
    },
  };
}
